import { useState } from "react";
import { Check, Loader2, Pencil, Plus } from "lucide-react";

import { cn } from "@/lib/utils";
import { TermChip } from "./TermChip";
import type { SaveStatus } from "@/hooks/useDebouncedSave";

export interface ColumnTerm {
  id: string;
  term: string;
  tag?: string | null;
  active: boolean;
}

/**
 * Una mitad del panel de intereses: cabecera con alcance y conteo, sus chips,
 * y el alta rápida en línea. Las ediciones se avisan hacia arriba; la columna
 * no sabe nada de persistencia.
 */
export function TermColumn({
  title,
  description,
  terms,
  emptyLabel,
  addPlaceholder,
  status,
  onAdd,
  onRemove,
  onToggle,
  onManage,
  className,
}: {
  title: string;
  description: React.ReactNode;
  terms: ColumnTerm[];
  emptyLabel: string;
  addPlaceholder: string;
  status: SaveStatus;
  onAdd: (term: string) => void;
  onRemove: (id: string) => void;
  onToggle: (id: string) => void;
  onManage: () => void;
  className?: string;
}) {
  const activeCount = terms.filter((item) => item.active).length;
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");

  function close() {
    setAdding(false);
    setDraft("");
  }

  // El término se envía tal cual: los espacios de borde son intencionales
  // (ej. "NiFF " no debe matchear "NIFFX").
  function commit() {
    if (!draft.trim()) return close();
    onAdd(draft);
    setDraft("");
  }

  return (
    <div className={cn("flex flex-col gap-2.5", className)}>
      <div className="flex min-h-6 flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="size-1.5 rounded-full bg-blue-600 dark:bg-blue-500" />
          <span className="text-[11px] font-extrabold uppercase tracking-wider">
            {title}
          </span>
          <span className="rounded-full bg-muted px-1.5 text-[11px] font-semibold text-muted-foreground">
            {activeCount === terms.length
              ? terms.length
              : `${activeCount} de ${terms.length}`}
          </span>
          {status !== "idle" && (
            <span className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
              {status === "saving" ? (
                <>
                  <Loader2 className="size-3 animate-spin" />
                  Guardando…
                </>
              ) : (
                <>
                  <Check className="size-3 text-emerald-600 dark:text-emerald-500" />
                  Guardado
                </>
              )}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onManage}
          className="flex flex-none cursor-pointer items-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950/50"
        >
          <Pencil className="size-3" />
          Gestionar
        </button>
      </div>

      <p className="text-[11.5px] leading-relaxed text-muted-foreground">
        {description}
      </p>

      <div className="flex flex-wrap items-start gap-1.5">
        {terms.length === 0 && !adding && (
          <span className="py-1 text-xs italic text-muted-foreground">
            {emptyLabel}
          </span>
        )}
        {terms.map((item) => (
          <TermChip
            key={item.id}
            term={item.term}
            tag={item.tag}
            active={item.active}
            onRemove={() => onRemove(item.id)}
            onToggle={() => onToggle(item.id)}
          />
        ))}

        {adding ? (
          <span className="inline-flex h-7 flex-none items-center gap-1.5 rounded-md border border-blue-500 bg-card pl-2.5 pr-1.5 ring-2 ring-blue-500/20 dark:border-blue-600">
            <input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={close}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commit();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  close();
                }
              }}
              placeholder={addPlaceholder}
              className="w-36 border-0 bg-transparent text-xs font-semibold outline-none placeholder:font-normal placeholder:text-muted-foreground"
            />
            <span className="text-[10px] text-muted-foreground">⏎</span>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex h-7 flex-none cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-dashed px-2.5 text-xs font-semibold text-muted-foreground transition-colors hover:border-blue-400 hover:text-blue-600 dark:hover:border-blue-700 dark:hover:text-blue-400"
          >
            <Plus className="size-3" />
            Añadir
          </button>
        )}
      </div>
    </div>
  );
}
